import { formatPercentage } from '@/domain/common/format'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getTokenImage } from '@/ui/assets/tokens'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PointsBadge } from '@/ui/molecules/points-badge/PointsBadge'
import { TokenSymbolEnum } from '@/utils/tokens'
import { formatCurrency } from '@coingecko/cryptoformat'
import { ExternalLink } from 'lucide-react'
import Image from 'next/image'
import { useSummaryInfo } from '../../logic/useSummaryInfo'

export function SummaryTiles() {
  const { usdxlPrice, borrowAPY, borrowPoints } = useSummaryInfo()

  const formattedPrice = formatCurrency(usdxlPrice.toNumber(), 'USD', 'en', true, {
    decimalPlaces: 5,
  })

  return (
    <div className="flex flex-col gap-4">
      {/* Hero stats: Price + Borrow APY */}
      <div className="grid grid-cols-2 gap-4">
        {/* Price card */}
        <Panel.Wrapper className="relative overflow-hidden p-5 sm:p-6">
          <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-primary/[0.04] to-transparent" />
          <div className="relative flex flex-col gap-2">
            <Typography variant="prompt" className="text-muted-foreground text-sm">
              USDXL Price
            </Typography>
            <div className="flex items-center gap-2">
              <Image
                src={getTokenImage(TokenSymbol('USDXL'))}
                className="h-7 w-7 rounded-full sm:h-8 sm:w-8"
                width={32}
                height={32}
                alt="USDXL"
              />
              <Typography variant="p" className="font-semibold text-2xl tracking-tight sm:text-4xl" gradient>
                {formattedPrice}
              </Typography>
            </div>
          </div>
        </Panel.Wrapper>

        {/* Borrow APY card */}
        <Panel.Wrapper className="relative overflow-hidden p-5 sm:p-6">
          <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-primary/[0.04] to-transparent" />
          <div className="relative flex flex-col gap-2">
            <Typography variant="prompt" className="text-muted-foreground text-sm">
              Borrow APY
            </Typography>
            <div className="flex items-center gap-2">
              <Typography variant="p" className="font-semibold text-2xl tracking-tight sm:text-4xl" gradient>
                {formatPercentage(borrowAPY)}
              </Typography>
              {typeof borrowPoints === 'number' && (
                <div className="self-center">
                  <PointsBadge
                    asset={TokenSymbol(TokenSymbolEnum.USDXL)}
                    action="borrow"
                    multiplier={borrowPoints}
                    size="lg"
                  />
                </div>
              )}
            </div>
          </div>
        </Panel.Wrapper>
      </div>

      {/* TradingView link */}
      <div className="px-1">
        <a
          href="https://www.tradingview.com/symbols/USDXLUSD/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-primary text-sm transition-colors hover:text-primary/80"
        >
          Track on TradingView
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  )
}
